// @flow
// $FlowFixMe
import React, { useEffect, useState } from "react";
import { easings, useSpring } from "@react-spring/web";
import Icon from "@fdr/static-ui/Icons";
import {
  successOutline,
  info,
  warningDiamond,
  hexagon,
  pin
} from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";

import {
  Wrapper,
  TitleSection,
  ContentWrapper,
  Title,
  SubTitle,
  Action
} from "./styled-components";

import type { ToastType } from "./styled-components";

export type ToastProps = {
  showToast?: boolean,
  callback?: Function,
  qaLabel?: string,
  type?: ToastType,
  title?: string,
  subTitle?: string,
  actionTitle?: string,
  onClickAction?: () => void,
  noAnimation?: boolean,
  renderBottom?: boolean,
  manualHideControl?: boolean,
  isTopPosition?: boolean,
  isSmallToast?: boolean
};

export const toastAnimationDuration = 350;

export const Toast = ({
  qaLabel = "toast",
  type,
  title,
  subTitle,
  actionTitle,
  onClickAction,
  showToast,
  manualHideControl,
  callback,
  noAnimation,
  renderBottom,
  isTopPosition = false,
  isSmallToast = false
}: ToastProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [visibility, setVisibility] = useState<"visible" | "hidden">("hidden");
  const animationControl = () => {
    if (isVisible) {
      const locationPosition = isTopPosition ? -50 : -20;
      return isSmallToast ? locationPosition : -65;
    }
    return 0;
  };

  const toastAnimationConfig = () => ({
    y: animationControl(),
    opacity: isVisible ? 1 : 0,
    config: {
      duration: noAnimation && isVisible ? 0 : toastAnimationDuration,
      easing: easings.easeInOutSine
    }
  });

  const controlToastOnRest = (controlProp) =>
    controlProp
      ? setVisibility("visible")
      : setTimeout(() => setVisibility("hidden"), toastAnimationDuration);

  const [toastStyles, toastApi] = useSpring(toastAnimationConfig, [isVisible]);

  useEffect(() => {
    if (showToast && !isVisible) {
      setIsVisible(showToast);
      controlToastOnRest(showToast);
    }

    if (manualHideControl && isVisible) {
      setIsVisible(showToast);
      controlToastOnRest(showToast);
    }
  }, [showToast]);

  useEffect(() => {
    if (isVisible && !manualHideControl) {
      setIsVisible(false);
      controlToastOnRest(isVisible);
      callback && callback();
    }

    if (isVisible && isSmallToast && type === "success" && manualHideControl) {
      setTimeout(() => setIsVisible(false), 5000);

      setTimeout(() => callback && callback(), 5000 + toastAnimationDuration);
    } else if (isVisible || manualHideControl) {
      toastApi.start(toastAnimationConfig());
    } else {
      setTimeout(() => toastApi.start(toastAnimationConfig()), 5000);
    }
  }, [isVisible, type]);

  const toastIcon = (toastType?: ToastType) => {
    switch (toastType) {
      case "info":
        return info;
      case "success":
        return isSmallToast ? pin : successOutline;
      case "warning":
        return warningDiamond;
      case "error":
        return hexagon;
      case "pending":
        return pin;
      default:
        return successOutline;
    }
  };

  return visibility === "visible" ? (
    <Wrapper
      data-qa-label={qaLabel}
      style={toastStyles}
      renderBottom={renderBottom || !isSmallToast}
      visibility={visibility}
    >
      <ContentWrapper
        type={type}
        isSmallToast={isSmallToast}
        hasAction={!!actionTitle}
      >
        <Icon size={17} icon={toastIcon(type)} color={buildColor("white")} />
        <TitleSection isSmallToast={isSmallToast}>
          <Title>{title}</Title>
          <SubTitle>{subTitle}</SubTitle>
        </TitleSection>
        {actionTitle && <Action onClick={onClickAction}>{actionTitle}</Action>}
      </ContentWrapper>
    </Wrapper>
  ) : null;
};

export default Toast;
